import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { SessionCard } from '../components/SessionCard';
import { CreateSessionModal } from '../components/CreateSessionModal';

interface GameSession {
  id: string;
  name: string;
  numPlayers: number;
  status: 'DRAFT' | 'SETUP' | 'ACTIVE' | 'COMPLETED';
  createdAt: string;
  startedAt?: string;
  endedAt?: string;
  stats?: {
    totalAudience: number;
    charactersCount: number;
    votesCount: number;
    challengesCount: number;
  };
}

function SessionManagement() {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState<GameSession[]>([]);
  const [activeSession, setActiveSession] = useState<GameSession | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [filter, setFilter] = useState<'all' | 'DRAFT' | 'ACTIVE' | 'COMPLETED'>('all');

  // Fetch sessions
  const fetchSessions = async () => {
    try {
      const queryParams = filter !== 'all' ? `?status=${filter}` : '';
      const response = await fetch(`/api/sessions${queryParams}`);
      const data = await response.json();

      if (data.success) {
        setSessions(data.sessions);
      }
    } catch (error) {
      console.error('Error fetching sessions:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch active session
  const fetchActiveSession = async () => {
    try {
      const response = await fetch('/api/sessions/active');
      if (response.ok) {
        const data = await response.json();
        setActiveSession(data.session);
      }
    } catch (error) {
      // No active session, that's ok
    }
  };

  useEffect(() => {
    fetchSessions();
    fetchActiveSession();
  }, [filter]);

  const handleCreateSession = async (name: string, numPlayers: number) => {
    try {
      const response = await fetch('/api/sessions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, numPlayers }),
      });

      const data = await response.json();

      if (data.success) {
        await fetchSessions();
        setIsCreateModalOpen(false);
      }
    } catch (error) {
      console.error('Error creating session:', error);
    }
  };

  const handleActivateSession = async (sessionId: string) => {
    try {
      const response = await fetch(`/api/sessions/${sessionId}/activate`, {
        method: 'POST',
      });

      const data = await response.json();

      if (data.success) {
        await fetchSessions();
        await fetchActiveSession();
      }
    } catch (error) {
      console.error('Error activating session:', error);
    }
  };

  const handleCloseSession = async (sessionId: string) => {
    if (!confirm('Are you sure you want to close this session? This action cannot be undone.')) {
      return;
    }

    try {
      const response = await fetch(`/api/sessions/${sessionId}/close`, {
        method: 'POST',
      });

      const data = await response.json();

      if (data.success) {
        await fetchSessions();
        await fetchActiveSession();
      }
    } catch (error) {
      console.error('Error closing session:', error);
    }
  };

  const handleDeleteSession = async (sessionId: string) => {
    if (!confirm('Are you sure you want to delete this session?')) {
      return;
    }

    try {
      const response = await fetch(`/api/sessions/${sessionId}`, {
        method: 'DELETE',
      });

      const data = await response.json();

      if (data.success) {
        await fetchSessions();
      }
    } catch (error) {
      console.error('Error deleting session:', error);
    }
  };

  return (
    <div className="container mx-auto p-8">
      {/* Back Button */}
      <button
        onClick={() => navigate('/admin')}
        className="text-blue-400 hover:text-blue-300 mb-4 flex items-center gap-2"
      >
        ← Back to Dashboard
      </button>

      {/* Header */}
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-4xl font-bold text-blue-400 mb-2">
            Session Management
          </h1>
          <p className="text-gray-400">
            Create and manage show sessions
          </p>
        </div>
        <button
          onClick={() => setIsCreateModalOpen(true)}
          className="bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-6 rounded-lg transition"
        >
          + New Session
        </button>
      </div>

      {/* Active Session Alert */}
      {activeSession && (
        <div className="bg-green-600 bg-opacity-20 border border-green-500 rounded-lg p-6 mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-xl font-bold text-green-400 mb-2">
                🎭 Active Session
              </h3>
              <p className="text-white font-semibold">{activeSession.name}</p>
              <p className="text-gray-400 text-sm">
                {activeSession.numPlayers} players • {activeSession.stats?.totalAudience || 0} audience members
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate(`/admin/session/${activeSession.id}/live`)}
                className="bg-purple-600 hover:bg-purple-700 text-white font-bold py-2 px-4 rounded transition"
              >
                🚀 Go Live
              </button>
              <button
                onClick={() => navigate(`/admin/session/${activeSession.id}`)}
                className="bg-gray-600 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded transition"
              >
                Manage Show
              </button>
              <button
                onClick={() => handleCloseSession(activeSession.id)}
                className="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded transition"
              >
                Close Session
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Filter Tabs */}
      <div className="flex gap-2 mb-6">
        {(['all', 'DRAFT', 'ACTIVE', 'COMPLETED'] as const).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-4 py-2 rounded-lg font-semibold transition ${
              filter === f
                ? 'bg-blue-600 text-white'
                : 'bg-gray-700 text-gray-400 hover:bg-gray-600'
            }`}
          >
            {f === 'all' ? 'All' : f}
          </button>
        ))}
      </div>

      {/* Sessions List */}
      {isLoading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="text-gray-400 mt-4">Caricamento sessioni...</p>
        </div>
      ) : sessions.length === 0 ? (
        <div className="text-center py-12 bg-gray-800 rounded-lg">
          <p className="text-gray-400 text-lg">Nessuna sessione trovata</p>
          <button
            onClick={() => setIsCreateModalOpen(true)}
            className="mt-4 text-blue-400 hover:text-blue-300 underline"
          >
            Crea la tua prima sessione
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {sessions.map((session) => (
            <SessionCard
              key={session.id}
              session={session}
              onActivate={handleActivateSession}
              onClose={handleCloseSession}
              onDelete={handleDeleteSession}
            />
          ))}
        </div>
      )}

      {/* Create Session Modal */}
      {isCreateModalOpen && (
        <CreateSessionModal
          onClose={() => setIsCreateModalOpen(false)}
          onCreate={handleCreateSession}
        />
      )}
    </div>
  );
}

export default SessionManagement;
